import { ACTIVE_JOB_STATES, JOB_RESOURCE, type ActivityJob, type ActivitySnapshot, type JobState, type StudioEvent } from '@studio/shared';

/** One Job as the shell knows it: from the snapshot, from the stream, or from both. */
export interface ActivityEntry {
  readonly id: string;
  readonly version: number;
  readonly state: JobState;
  readonly at: string;
  /** Present once a snapshot has described this Job. The stream alone carries no detail. */
  readonly job: ActivityJob | null;
}

export interface ActivityState {
  readonly snapshotApplied: boolean;
  readonly jobs: readonly ActivityEntry[];
  readonly activeCount: number;
  /** Jobs seen only on the stream. A snapshot read fills them in when detail is wanted. */
  readonly unknown: readonly string[];
}

export interface ActivityReconciler {
  /** Feed one stream event. True when it changed anything. */
  receive(event: StudioEvent): boolean;
  applySnapshot(snapshot: ActivitySnapshot): void;
  state(): ActivityState;
}

/**
 * The reconnect protocol, client side.
 *
 * The browser opens the stream first and buffers, then reads the REST snapshot, then
 * applies only what is newer than the snapshot. That order is what closes the window
 * between the two: a change that lands inside it arrives on the stream and survives
 * the snapshot, and a change the snapshot already contains is dropped by version
 * rather than applied a second time. No journal is replayed; PostgreSQL is the truth.
 */
export function createActivityReconciler(): ActivityReconciler {
  const entries = new Map<string, ActivityEntry>();
  const buffered: StudioEvent[] = [];
  let snapshotApplied = false;

  const isActive = (state: JobState): boolean => ACTIVE_JOB_STATES.includes(state);

  const apply = (event: StudioEvent): boolean => {
    const standing = entries.get(event.id);
    // Older or repeated: the version is the whole test, and it is monotonic per resource.
    if (standing !== undefined && event.version <= standing.version) return false;
    entries.set(event.id, {
      id: event.id,
      version: event.version,
      state: (event.state ?? 'queued') as JobState,
      at: event.at,
      job: standing?.job ?? null,
    });
    return true;
  };

  return {
    receive(event) {
      if (event.resource !== JOB_RESOURCE) return false;
      if (!snapshotApplied) {
        buffered.push(event);
        return true;
      }
      return apply(event);
    },

    applySnapshot(snapshot) {
      entries.clear();
      for (const job of snapshot.jobs) {
        entries.set(job.id, { id: job.id, version: job.version, state: job.state, at: job.at, job });
      }
      snapshotApplied = true;
      for (const event of buffered.splice(0)) apply(event);
    },

    state() {
      const jobs = [...entries.values()];
      return {
        snapshotApplied,
        jobs,
        activeCount: snapshotApplied ? jobs.filter((entry) => isActive(entry.state)).length : 0,
        unknown: jobs.filter((entry) => entry.job === null).map((entry) => entry.id),
      };
    },
  };
}
